import { InputText } from "primereact/inputtext";
import { forwardRef, useImperativeHandle, useRef } from "react";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { textService } from "src/core/services/textService";
import type { FocusRef } from "../types/FocusRef";

export interface NameFormData extends FieldValues {
  name: string;
}

type NameProps<T extends NameFormData> = {
  form: UseFormReturn<T, any, T>;
  required: boolean;
};

export interface NameRef extends FocusRef {}

export const NameField = forwardRef<NameRef, NameProps<any>>(function NameField<
  T extends NameFormData
>({ form, required = true }: NameProps<T>, ref: React.ForwardedRef<NameRef>) {
  const nameRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      nameRef.current?.focus();
    },
  }));

  const { t } = useTranslation();

  const error = form.formState.errors.name as Record<string, any> | undefined;

  return (
    <>
      <InputText
        id="name"
        autoComplete="name"
        className="w100"
        invalid={!!error}
        {...form.register("name" as Path<T>, {
          required: required && t("Error.Required"),
          minLength: {
            value: 3,
            message: t("Error.MinLength", { value: 3 }),
          },
          onChange: (e) => {
            e.target.value = textService.toTitleCase(e.target.value);
            return e;
          },
        })}
        ref={(e) => {
          form.register("name" as Path<T>).ref(e);
          nameRef.current = e;
        }}
        type="text"
      />
      {error && <small className="p-error block">{error?.message}</small>}
    </>
  );
});
